import PopupManager from "../../Common/Popups/PopupManager";
import { parseString } from "../../Utilities/Generic/Strings";
import UUID from "../../Utilities/Generic/UUID";
import CDN from "../../Utilities/Platform/CDN";
import { getOption } from "../../Utilities/Platform/Options/Options";
import { NodeWorkerPolyfill } from "../NodeWorkerPolyfill";
import { WorkerPolyfill } from "../WorkerPolyfill";
import { AGENT_CLOSE_EVENT, AGENT_CONNECTION_READY, AGENT_PING_EVENT } from "./AgentEvents";
import AgentManager from "./AgentManager";
import { WORKER_PORT_CALLBACK, WORKER_PORT_CLEAR_CALLBACKS, WORKER_PORT_CLOSE_EVENT } from "./WorkerPort";

declare let globalThis: {
    waitForAllWorkerImports?: () => Promise<void>;
};

const WORKER_SUFFIX = (process.env.NODE_ENV === "production" ? `.${__VERSION__.commit}` : "");
const WORKER_LOCATION_PREFIX = "/dist/";

export const generateWorkerURL = (name: string): string => {
    if(process.env.USING_CDN_PUBLIC_PATH) {
        return CDN(
            `pos/spa/${__VERSION__.channel}/${__VERSION__.commit}/${name}${WORKER_SUFFIX}.bundle.js?worker=true`,
            false
        );
    }

    return `${WORKER_LOCATION_PREFIX}${name}${WORKER_SUFFIX}.bundle.js?worker=true`;
};

export default abstract class BaseAgent {
    protected worker!: SharedWorker | WorkerPolyfill | NodeWorkerPolyfill;
    protected store: string;
    private checkingForRestart = false;
    private failureCount = 0;
    private shouldStartWhenReady = false;
    private isReady = false;
    private onReady = new Set<() => void>();
    private pingEventId = "";
    protected callbacks: Record<string, Function>;

    protected constructor(
        protected url: string,
        protected name: string,
        protected forceMode?: "dedicated",
        explicitCreate = false,
        private automaticRestart = true
    ) {
        this.handleWorkerMessage = this.handleWorkerMessage.bind(this);
        this.sendMessage = this.sendMessage.bind(this);

        if(!explicitCreate) {
            if(process.env.NODE_ENV !== "test") {
                setTimeout(() => {
                    // Spawn the worker at a random time within one seconds of load
                    this.spawnWorker()
                        .then(() => this.checkIfWorkerShouldStart());
                }, Math.random() * 1000);
            } else {
                this.spawnWorker().then(() => this.checkIfWorkerShouldStart());
            }
        }

        AgentManager.addAgent(this);

        // Get the url of the store
        this.store = "";

        if(typeof window !== "undefined") {
            this.store = window.location.host.split(".")[0];
        }

        this.callbacks = {};

        if(this.automaticRestart && process.env.NODE_ENV !== "test") {
            // Check once every 10 seconds
            setTimeout(() => {
                // Randomise the ping slightly so not all workers respond at the same time
                setInterval(this.checkIfAutomaticRestartIsRequired, 10000);
            }, Math.random() * 10000);
        }
    }

    /**
     * Get a worker wrapper URL
     */
    protected getWorkerWrapperUrl(toLoad: string, mode: string): string {
        const base = `https://${window.location.host}/worker-wrapper.js?load=${btoa(toLoad)}&name=${this.name}&version=${__VERSION__.commit}&store=${this.store}&mode=${mode}`;

        const forceSkipCache = getOption("experiment_force_worker_skip_cache");

        if(forceSkipCache === false) {
            return base;
        }

        if(forceSkipCache || process.env.NODE_ENV !== "production") {
            return `${base}&alwaysLoad=true`;
        }

        return base;
    }

    /**
     * Store callback and return new id
     */
    protected createCallback(callback: Function): string {
        const callbackId = UUID.generate();

        this.callbacks[callbackId] = callback;

        return callbackId;
    }

    /**
     * Call the stored callback based of the id
     */
    protected handleCallback(id: string, args = []): void {
        if(typeof this.callbacks[id] === "undefined") {
            return;
        } else if(typeof this.callbacks[id] === "function") {
            if(!args) {
                this.callbacks[id]();
            } else {
                this.callbacks[id](...args);
            }
        }

        this.deleteCallback(id);
    }

    protected deleteCallback(id: string): void {
        delete this.callbacks[id];
    }

    protected startWorker(): void {
        if(typeof this.worker !== "undefined") {
            this.worker.port.addEventListener("message", this.handleWorkerMessage);
            this.worker.port.addEventListener("messageerror", this.handleWorkerMessageError);
            this.worker.port.start();
        } else {
            this.shouldStartWhenReady = true;
        }
    }

    private checkIfWorkerShouldStart(): void {
        if(this.shouldStartWhenReady) {
            return this.startWorker();
        }
    }

    private isSafari(): boolean {
        // Safari has support for shared workers, but they can't be debugged, so we can't determine what issues
        // there are when any issue is presented
        // From https://stackoverflow.com/a/31732310
        if(process.env.NODE_ENV === "test") {
            // During tests, the vendor can return the device name, which may include Apple when running on macbooks
            return false;
        }

        return !!(
            navigator.vendor &&
            navigator.vendor.indexOf("Apple") > -1
        );
    }

    protected async spawnWorker(): Promise<void> {
        if(process.env.NODE_ENV !== "test") {
            console.log("Attempting to start the worker", this.name);
        } else if(typeof globalThis.waitForAllWorkerImports !== "undefined") {
            await globalThis.waitForAllWorkerImports();
        }

        const forceMode = this.forceMode;
        let url = this.url;
        const name = this.name;

        let WorkerType;
        let usingNodePolyfill = false;
        let mode;

        if(
            typeof SharedWorker !== "undefined" &&
            parseString(process.env.FORCE_WORKER as string) !== true &&
            typeof forceMode === "undefined" &&
            !this.isSafari()
        ) {
            WorkerType = SharedWorker;
            mode = "shared";
        } else if(typeof Worker !== "undefined") {
            WorkerType = WorkerPolyfill;
            mode = "dedicated";
        } else if(forceMode === "dedicated" && process.env.NODE_ENV === "test") {
            WorkerType = NodeWorkerPolyfill;
            usingNodePolyfill = true;
            mode = "dedicated";
        } else {
            PopupManager.create({
                id     : "workers-unsupported",
                message: "Background workers are not supported in this browser, please update your browser.",
                icon   : "exclamation",
                classes: [ "popup-error" ],
                timeout: 0,
            });

            throw new Error("Workers are not supported in this browser");
        }

        // Append the version to the URL
        let joiner = "?";

        if(url.includes("?")) {
            joiner = "&";
        }

        if(!usingNodePolyfill) {
            url = `${url}${joiner}version=${__VERSION__.commit}`;
        }

        const worker = new WorkerType(
            this.getWorkerWrapperUrl(url, mode),
            name
        );

        if(typeof worker === "undefined") {
            throw new Error("Agent was constructed with invalid worker given");
        }

        this.worker = worker;
    }

    protected terminateWorker(): void {
        console.log("Attempting to terminate the worker", this.name);
        this.isReady = false;

        if(typeof this.worker !== "undefined") {
            if(typeof SharedWorker !== "undefined" && this.worker instanceof SharedWorker) {
                // Shared workers can just close the ports
                this.worker.port.close();
            } else {
                (this.worker as WorkerPolyfill).terminate();
            }
        }
    }

    protected handleWorkerMessage(e: MessageEvent): void {
        if(e.data === "relaunchAsDedicated") {
            this.pingEventId = "";
            this.failureCount = 0;
            console.log(`Relaunching worker ${this.name} as a dedicated worker due to shared worker request`);
            this.terminateWorker();
            this.forceMode = "dedicated";
            this.spawnWorker()
                .then(() => {
                    this.startWorker();
                });

            return;
        }

        if(!e.data.type) {
            console.error("Unknown data passed to base agent");
        }

        if(e.data.store !== this.store) {
            return;
        }

        switch(e.data.type) {
            case WORKER_PORT_CLOSE_EVENT:
                // TODO: HANDLE CLOSE
                break;
            case WORKER_PORT_CALLBACK:
                this.handleCallback(e.data.message.id, e.data.message.args);
                break;
            case WORKER_PORT_CLEAR_CALLBACKS:
                // Delete all callbacks
                for(let i = 0; i < e.data.message.length; i++) {
                    this.deleteCallback(e.data.message[i]);
                }

                break;
            case AGENT_CONNECTION_READY:
                if(process.env.NODE_ENV !== "test") {
                    console.log(`${this.name} has established a connection`);
                }

                this.isReady = true;
                this.onReady.forEach(callback => callback());
                this.onReady.clear();
                break;
            default:
                this.handleMessage(e.data);
                break;
        }
    }

    protected handleWorkerMessageError(e: MessageEvent): void {
        console.warn("Could not deserialize a from a worker message and received an error", e);
    }

    protected abstract handleMessage(data: any): void;

    /**
     * Prepares message object to be sent (recursive for objects)
     */
    protected prepareValue(message: any): any {
        const type = typeof message;

        if(type === "function") {
            return this.prepareFunction(message);
        } else if(Array.isArray(message)) {
            return this.prepareArray(message);
        } else if(type === "object") {
            return this.prepareObject(message);
        }

        return message;
    }

    /**
     * Prepares objects to be sent
     */
    protected prepareObject(object: Record<string, any>): Record<string, any> {
        if(object === null) {
            return object;
        }

        const results: Record<string, any> = {};

        for(const key in object) {
            if(Object.prototype.hasOwnProperty.call(object, key)) {
                // Recursively prepare object
                results[key] = this.prepareValue(object[key]);
            }
        }

        return results;
    }

    /**
     * Prepares array to be sent
     */
    protected prepareArray(array: Array<any>): Array<any> {
        const results = [];

        for(let i = 0, l = array.length; i < l; i++) {
            results.push(this.prepareValue(array[i]));
        }

        return results;
    }

    /**
     * Prepares functions for callbacks to be sent
     */
    protected prepareFunction(callback: Function | null): { __callback: string; } | null {
        if(typeof callback !== "function") {
            return callback;
        }

        return {
            __callback: this.createCallback(callback),
        };
    }

    public sendMessage(event: string, message: any = null, success: Function | null = null): void {
        // TODO: ADD USE OF ERROR CALLBACK
        if(!this.isReady) {
            this.onReady.add(() => this.sendMessage(event, message, success));

            return;
        }

        if(typeof this.worker !== "undefined") {
            this.worker.port.postMessage({
                type   : event,
                store  : this.store,
                message: this.prepareValue(message),
                success: this.prepareFunction(success),
            });
        } else {
            console.log(
                `Attempting to send a message from ${this.constructor.name} before the worker has been created`
            );
        }
    }

    private checkIfAutomaticRestartIsRequired = async (): Promise<void> => {
        if(this.checkingForRestart || this.failureCount > 3) {
            return;
        }

        this.checkingForRestart = true;
        const id = Math.random().toString();

        this.pingEventId = id;
        const result = await new Promise(res => {
            let hasTimedOut = false;
            const timeout = setTimeout(() => {
                hasTimedOut = true;
                res(false);
            }, 1000);

            this.sendMessage(AGENT_PING_EVENT, {}, () => {
                if(hasTimedOut) {
                    return;
                }

                clearTimeout(timeout);
                res(true);
            });
        });

        if(id !== this.pingEventId) {
            return;
        }

        this.checkingForRestart = false;

        if(process.env.NODE_ENV === "test") {
            return; // Do nothing when testing
        }

        if(!result) {
            console.warn(`The ${this.name} worker has failed to restart, retrying`);
            this.failureCount++;

            if(this.failureCount > 3) {
                console.warn(
                    `The ${this.name} worker has failed three times in a row, showing notification`
                );
                PopupManager.create({
                    id     : "worker-failed",
                    message: (
                        <a
                            href="#"
                            onClick={e => {
                                e.preventDefault();
                                window.location.reload();
                            }}
                        >
                            A background worker has failed to restart correctly,
                            please click here when you can to refresh the page.
                            If this keeps appearing, please contact Shopfront.
                            The POS will not work correctly until this is done.
                        </a>
                    ),
                    icon   : "exclamation",
                    classes: [ "popup-error" ],
                    timeout: 0,
                });
            } else {
                this.terminateWorker();
                void this.spawnWorker();
            }
        } else {
            this.failureCount = 0;
        }
    };

    public close(): void {
        if(process.env.NODE_ENV === "test") {
            if(Object.keys(this.callbacks).length) {
                throw new Error(`There are outstanding agent callbacks waiting for the ${this.constructor.name} agent`);
            }

            return;
        }

        this.sendMessage(AGENT_CLOSE_EVENT);
    }

    /**
     * Get the worker when it's ready, this should only be used in testing
     */
    protected getWorker(): Promise<typeof this.worker> {
        const checkForWorkerReadiness = () => {
            return this.worker && (this.worker as any).port["worker"]; // MockSharedWorker
        };

        return new Promise(res => {
            if(checkForWorkerReadiness()) {
                res(this.worker);

                return;
            }

            const interval = setInterval(() => {
                if(checkForWorkerReadiness()) {
                    clearInterval(interval);

                    res(this.worker);
                }
            }, 5);
        });
    }
}
